(ns ui.view.card.risks.view
  (:require
   [ui.view.card.risks.sub :as sub]
   [ui.view.card.risks.event :as event]
   [ui.view.card.shared-components.card-title :as card-title]
   [ui.view.card.shared-components.card-container :as card-container]
   [ui.view.card.shared-components.entity-table :as entity-table]
   [ui.view.card.shared-components.entity-peek :as entity-peek]
   [ui.view.card.event :as common-event]
   [ui.view.card.sub :as common-sub]
   [common.localization :as loc]
   [re-frame.core :as rf]
   [ui.view.card.risks.model :as model]
   [ui.view.card.entity-list.view :as entity-list-view]
   [ui.components.button.view :as button]
   [frontend.view.entity-viewers.modules.risk.event :as risk-module]
   [ui.view.card.shared-components.view :as card-shared]
   [ui.view.card.shared-components.full-screen-card :as full-screen-card]))

(defn add-new-risk-button
  [{:keys [entity-id preview-mode? primary-button? ctx] :as _props}]
  (let [component (if primary-button?
                    button/sm-primary-solid
                    button/sm-secondary-hollow)]
    [component
     {:disabled? preview-mode?
      ;; TODO how to get newly created risk into card-specific entities?
      :on-click #(rf/dispatch [::risk-module/create-risk entity-id])}
     [:i {:class "fal fa-plus"}]
     (loc/tr :add-risk)]))

(defn empty-state-view
  [props]
  [:div
   {:class "flex flex-col items-center gap-4 py-5"}
   [:div
    {:class "relative"}
    [:i {:class "fal fa-comments-dollar text-5xl font-thin"}]
    [:div
     {:class "absolute -bottom-1 -right-2 flex items-center justify-center bg-base-light rounded-full w-7 h-7"}
     [:i {:class "fal fa-plus text-2xl"}]]]
   [:p
    {:class "text-lg font-semibold"}
    (loc/tr :no-risks-yet)]
   [:div
    {:class "flex justify-center"}
    [add-new-risk-button
     (assoc props :primary-button? true)]]])

(defn delete-risk-confirmation-modal
  [{:keys [ctx]}]
  (let [helper-text @(rf/subscribe [::sub/delete-risk-helper-text ctx])]
    [:dialog
     {:id (model/delete-risk-confirmation-modal-id ctx)
      :class "border rounded p-0"}
     [:div
      {:class "relative p-3 flex flex-col gap-3 w-lg max-w-lg"}
      [:div
       {:class "absolute top-2 right-2"}
       [button/square-secondary-open
        {:on-click #(rf/dispatch [::event/close-delete-confirmation-modal ctx])}
        [:i {:class "fal fa-times"}]]]
      [:div
       {:class "flex justify-center mt-3"}
       [:i
        {:class "fal fa-exclamation-triangle text-2xl text-danger"}]]

      [:div
       [:h4
        {:class "text-center text-lg"}
        "Delete risk?"]
       [:p
        {:class "text-center text-base-light-text text-sm"}
        helper-text]]

      [:div
       {:class "flex justify-center gap-2"}
       [button/sm-secondary-hollow
        {:on-click #(rf/dispatch [::event/close-delete-confirmation-modal ctx])}
        "Cancel"]
       [button/sm-danger-solid
        {:on-click #(rf/dispatch [::event/delete-risk ctx])}
        "Delete"]]]]))

(defn table-view
  [{:keys [card ctx]}]
  (let [table-data @(rf/subscribe [::sub/table-data ctx])
        columns (-> card :card/config :table-config :columns)
        peek-entity-id @(rf/subscribe [::common-sub/entity-peek-entity-id ctx])]
    [:div
     {:class "flex flex-col gap-5 grow"}
     [entity-table/view
      {:columns columns
       :config (-> card :card/config :table)
       :data table-data
       :selected-row-id peek-entity-id
       :on-row-clicked (fn [{:keys [entity-id]}]
                         (rf/dispatch [::common-event/open-entity-peek-or-close-if-already-open
                                       ctx entity-id]))}]]))

(defn entity-peek-view
  [{:keys [card preview-mode? ctx]}]
  (let [open? @(rf/subscribe [::common-sub/entity-peek-open? ctx])
        {:keys [entity-id]} ctx
        peek-entity-id @(rf/subscribe [::common-sub/entity-peek-entity-id ctx])
        risk @(rf/subscribe [::common-sub/entity-peek-entity ctx])
        entity-peek-config (-> card :card/config :entity-peek)
        entity-peek-data @(rf/subscribe [::common-sub/entity-peek-data ctx entity-peek-config])]
    [entity-peek/view
     {:open? open?
      :data entity-peek-data
      ;;TODO risk-entity-type based on containing-entity-type:
      :entity-type :entity.type/risk-holding
      :close (fn []
               (rf/dispatch [::common-event/close-entity-peek ctx]))
      :preview-mode? preview-mode?
      :edit (fn []
              ;; TODO updated risk into card entities:
              ;; OR copy-paste edit&create modal specific functionality here
              ;; note: this risk-editor-modal also used for deleting:
              (rf/dispatch [::risk-module/open-risk entity-id risk
                            #_event-on-success]))
      :open-remove-confirmation
      (fn []
        (rf/dispatch [::event/open-delete-confirmation-modal ctx peek-entity-id]))}]))

(defn component
  [{:keys [card ctx] :as props}]
  (let [title (:card/title card)
        risks-count @(rf/subscribe [::sub/risks-count ctx])
        risks? (pos? risks-count)
        loading? @(rf/subscribe [::common-sub/loading? ctx])]
    [card-container/view
     {}
     [:div
      {:class "flex"}
      [:div
       {:class "flex flex-col gap-2 grow"}
       [:div
        {:class "flex justify-between min-h-12 items-start"}
        [card-title/view title]
        [:div
         {:class "flex gap-2 items-center"}
         (when risks?
           [add-new-risk-button props])
         [card-shared/expand-card props]]]
       (cond
         loading? [entity-list-view/loading-skeleton]
         risks? [table-view props]
         :else [empty-state-view props])]
      [entity-peek-view props]]]))

(defn full-screen-component
  [{:keys [ctx] :as props}]
  (let [risks-count @(rf/subscribe [::sub/risks-count ctx])
        loading? @(rf/subscribe [::common-sub/loading? ctx])]
    [full-screen-card/view
     props
     [:div
      {:class "flex w-full h-full"}
      (cond
        loading? [entity-list-view/loading-skeleton]
        (pos? risks-count) [table-view props]
        :else [empty-state-view props])
      [entity-peek-view props]]]))

(defn view
  [{:keys [full-screen?] :as props}]
  [:<>
   (if full-screen?
     [full-screen-component props]
     [component props])
   [delete-risk-confirmation-modal props]])
